import React, { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Film, Star } from "lucide-react";

const AUTOPLAY_MS = 3500;
const SWIPE_THRESHOLD = 40;

/**
 * Animated hero showcase for the landing page's right side — one story
 * cover fully visible at a time, sliding in from the right and the outgoing
 * one sliding out to the left every few seconds (matches the sibling Little
 * Verses app's hero: a single card that swaps, not a multi-card peek deck).
 * Takes `stories` as data (`{ image, title }[]`) rather than hardcoding
 * slide markup, so it works with however many covers are passed in.
 */
export default function StoryHeroCarousel({ stories }) {
  const n = stories.length;
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = advancing (next), -1 = going back (prev)
  const [hovered, setHovered] = useState(false);
  const touchStartX = useRef(null);
  const reducedMotion = useRef(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  const goTo = useCallback((i, dir) => {
    setDirection(dir);
    setActive(((i % n) + n) % n);
  }, [n]);
  const next = useCallback(() => goTo(active + 1, 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1, -1), [active, goTo]);

  useEffect(() => {
    if (n < 2 || hovered || reducedMotion.current) return;
    const id = setInterval(() => setActive((a) => (a + 1) % n), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [hovered, n]);

  if (!n) return null;
  const story = stories[active];

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx <= -SWIPE_THRESHOLD) next();
    else if (dx >= SWIPE_THRESHOLD) prev();
    touchStartX.current = null;
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
  };

  const duration = reducedMotion.current ? 0.01 : 0.55;
  const variants = {
    enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
  };

  return (
    <div
      className="relative select-none"
      data-testid="story-hero-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="HikmahVerses story covers"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="hv-card p-3 sm:p-4 relative overflow-hidden" data-testid="hero-mockup">
        {/* aspect-[4/5] matches the covers' real 700x872 dimensions exactly
            (confirmed via sharp metadata) — object-contain on top so even a
            future cover with a slightly different ratio never gets cropped;
            the whole cover, including its own baked-in title, must always
            be fully visible. */}
        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-hv-navy">
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <motion.img
              key={story.image}
              src={story.image}
              alt={story.title}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-contain"
              draggable={false}
            />
          </AnimatePresence>
        </div>

        {/* Floating badges — same corner treatment as the section's other
            hv-card mockups. */}
        <div className="absolute -top-4 -start-4 hidden sm:flex hv-card px-4 py-3 items-center gap-2 z-20" data-testid="floating-new-story">
          <div className="hv-icon-circle-coral w-9 h-9 rounded-full flex items-center justify-center">
            <Film size={16} strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.2em] text-hv-teal font-bold">New story</div>
            <div className="hv-heading text-hv-navy text-sm">Every Monday</div>
          </div>
        </div>
        <div className="absolute -bottom-4 -end-4 hidden sm:flex hv-card px-4 py-3 items-center gap-2 z-20" data-testid="floating-quiz-earned">
          <div className="hv-icon-circle-gold w-9 h-9 rounded-full flex items-center justify-center">
            <Star size={16} fill="#B27E0A" strokeWidth={0} />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.2em] text-hv-teal font-bold">Quiz earned</div>
            <div className="hv-heading text-hv-navy text-sm">+3 stars</div>
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      <div className="mt-5 flex items-center justify-center gap-1.5" data-testid="story-carousel-dots">
        {stories.map((s, i) => (
          <button
            key={s.image}
            type="button"
            onClick={() => goTo(i, i > active ? 1 : -1)}
            aria-label={`Go to story ${i + 1}: ${s.title}`}
            aria-current={i === active}
            className={`rounded-full transition-all duration-300 ${
              i === active ? "w-5 h-1.5 bg-hv-coral" : "w-1.5 h-1.5 bg-hv-navy/20 hover:bg-hv-navy/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
